/**
 * useGlobeLayerState — Globe-mode layer toggle state (S3.2 flat 10-toggle).
 *
 * PRD FR-5.5 + cleanup plan §3 (S3.0 amendment 2026-05-26): flat 10-toggle
 * set, not a hierarchy. `investigations` is a peer toggle that does NOT map
 * to a backend SignalDomain. The `other` SignalDomain falls through (no
 * toggle); treated as always-visible to avoid hiding uncategorised signals.
 *
 * Persists to localStorage at `echelon.globe.layers` (AC-3.2.a).
 */

import { useCallback, useState } from 'react';
import type { SignalDomain } from '../api/worldMonitor';

// ── Canonical 10 toggles ───────────────────────────────────────────

export type GlobeLayerKey =
  | 'geopolitical'
  | 'markets'
  | 'maritime'
  | 'aviation'
  | 'seismic'
  | 'environment-health'
  | 'cyber'
  | 'nuclear'
  | 'sports'
  | 'investigations';

export interface GlobeLayerDef {
  /** Toggle key persisted to localStorage. */
  id: GlobeLayerKey;
  /** Visible label. */
  label: string;
  /** Backend SignalDomain values mapped to this toggle. Empty for `investigations`. */
  domains: SignalDomain[];
  /** Token-bound hue used by the UI dot indicator. */
  color: string;
  /** Default value on first load. */
  default: boolean;
}

export const GLOBE_LAYER_DEFS: readonly GlobeLayerDef[] = [
  { id: 'geopolitical',       label: 'Geopolitical',        domains: ['geopolitical'],                              color: 'var(--sig-iris)',  default: true  },
  { id: 'markets',            label: 'Markets',             domains: ['finance', 'corporate', 'legal', 'academic'], color: 'var(--sig-ember)', default: true  },
  { id: 'maritime',           label: 'Maritime',            domains: ['maritime'],                                   color: 'var(--sig-azure)', default: true  },
  { id: 'aviation',           label: 'Aviation',            domains: ['aviation'],                                   color: 'var(--sig-azure)', default: true  },
  { id: 'seismic',            label: 'Seismic',             domains: ['seismic'],                                    color: 'var(--sig-amber)', default: true  },
  { id: 'environment-health', label: 'Environment + Health', domains: ['environmental', 'health'],                   color: 'var(--sig-mint)',  default: true  },
  { id: 'cyber',              label: 'Cyber',               domains: ['cyber'],                                      color: 'var(--sig-rose)',  default: true  },
  { id: 'nuclear',            label: 'Nuclear',             domains: ['nuclear'],                                    color: 'var(--sig-amber)', default: false },
  { id: 'sports',             label: 'Sports',              domains: ['sports'],                                     color: 'var(--sig-mint)',  default: false },
  { id: 'investigations',     label: 'Investigations',      domains: [],                                             color: 'var(--fg)',        default: true  },
] as const;

/** Layer defs keyed by id, for lookups in render code. */
export const GLOBE_LAYER_DEFS_BY_ID: Record<GlobeLayerKey, GlobeLayerDef> = GLOBE_LAYER_DEFS.reduce(
  (acc, def) => {
    acc[def.id] = def;
    return acc;
  },
  {} as Record<GlobeLayerKey, GlobeLayerDef>,
);

/**
 * Legacy export retained for any code that still iterates a record-shaped
 * map of layer defs. S3.2 produces the same {label, domains, color} shape.
 */
export const GLOBE_SIGNAL_LAYERS: Record<GlobeLayerKey, { label: string; domains: SignalDomain[]; color: string }> =
  GLOBE_LAYER_DEFS.reduce(
    (acc, def) => {
      acc[def.id] = { label: def.label, domains: def.domains, color: def.color };
      return acc;
    },
    {} as Record<GlobeLayerKey, { label: string; domains: SignalDomain[]; color: string }>,
  );

// ── Domain → toggle reverse lookup ─────────────────────────────────

const DOMAIN_TO_TOGGLE: Partial<Record<SignalDomain, GlobeLayerKey>> = {};
for (const def of GLOBE_LAYER_DEFS) {
  for (const domain of def.domains) {
    DOMAIN_TO_TOGGLE[domain] = def.id;
  }
}

// ── State shape ────────────────────────────────────────────────────

export type GlobeLayerState = Record<GlobeLayerKey, boolean>;

function defaultsState(): GlobeLayerState {
  const out = {} as GlobeLayerState;
  for (const def of GLOBE_LAYER_DEFS) {
    out[def.id] = def.default;
  }
  return out;
}

/**
 * Check if a SignalDomain is currently visible per the layer state.
 * Returns true for `other` and any domain not mapped to a toggle so
 * uncategorised signals stay visible by default.
 */
export function isDomainVisible(domain: SignalDomain | string, state: GlobeLayerState): boolean {
  const key = DOMAIN_TO_TOGGLE[domain as SignalDomain];
  if (!key) return true;
  return state[key];
}

// ── Domain-scope derivation (FE-1 sprint-241, FR-S6.3) ─────────────

/**
 * Best-effort knowledge-domain → layer-toggle resolution for the
 * `/workspace?globe=1&domain=<d>` deep-link. Mapping (in order):
 *   1. exact GlobeLayerKey match  — 'maritime' → maritime,
 *      'geopolitical' → geopolitical, 'seismic' → seismic, …
 *   2. SignalDomain membership    — 'finance' / 'corporate' / 'legal' /
 *      'academic' → markets; 'environmental' / 'health' →
 *      environment-health.
 *   3. no match → null            — e.g. 'space-weather', 'risk': the
 *      caller keeps the current layer state UNCHANGED (honest no-op
 *      scoping; we do NOT invent layer groups) and the takeover stays open.
 */
export function resolveDomainLayerKey(domain: string): GlobeLayerKey | null {
  const d = domain.trim().toLowerCase();
  if (!d) return null;
  for (const def of GLOBE_LAYER_DEFS) {
    if (def.id === d) return def.id;
    if ((def.domains as string[]).includes(d)) return def.id;
  }
  return null;
}

/**
 * Derive a domain-scoped layer state: the matched group ON, every other
 * signal-domain group OFF. The `investigations` toggle (empty `domains`)
 * is NOT a signal-domain group and keeps its current value. Returns null
 * for unknown domains (caller keeps `base` unchanged).
 */
export function deriveDomainScopedLayerState(
  domain: string,
  base: GlobeLayerState,
): GlobeLayerState | null {
  const key = resolveDomainLayerKey(domain);
  if (!key) return null;
  const next = { ...base };
  for (const def of GLOBE_LAYER_DEFS) {
    if (def.domains.length === 0) continue; // 'investigations' — untouched
    next[def.id] = def.id === key;
  }
  return next;
}

// ── Persistence ────────────────────────────────────────────────────

export const STORAGE_KEY = 'echelon.globe.layers';

function loadFromStorage(): Partial<GlobeLayerState> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const valid: Partial<GlobeLayerState> = {};
    for (const def of GLOBE_LAYER_DEFS) {
      const v = parsed[def.id];
      if (typeof v === 'boolean') valid[def.id] = v;
    }
    return valid;
  } catch {
    return null;
  }
}

function saveToStorage(state: GlobeLayerState) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage quota or disabled — silently degrade
  }
}

// ── Hook ───────────────────────────────────────────────────────────

export function useGlobeLayerState() {
  const [state, setState] = useState<GlobeLayerState>(() => ({
    ...defaultsState(),
    ...(loadFromStorage() ?? {}),
  }));

  const toggle = useCallback((key: GlobeLayerKey) => {
    setState((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveToStorage(next);
      return next;
    });
  }, []);

  const resetToDefaults = useCallback(() => {
    const defaults = defaultsState();
    setState(defaults);
    saveToStorage(defaults);
  }, []);

  /**
   * Apply a best-effort domain scope (sprint-241 FR-S6.3 deep-link).
   * Unknown domains are an honest no-op. Deliberately NOT persisted to
   * localStorage: the scope is a navigation hint, not an operator
   * preference — the next manual toggle persists whatever is then visible.
   */
  const applyDomainScope = useCallback((domain: string) => {
    setState((prev) => deriveDomainScopedLayerState(domain, prev) ?? prev);
  }, []);

  const enabledCount = Object.values(state).filter(Boolean).length;

  return { state, toggle, resetToDefaults, applyDomainScope, enabledCount };
}
